import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Card,
  Divider,
  Empty,
  Flex,
  Form,
  Skeleton,
  Tooltip,
} from 'antd-v5';
import { DateTime } from 'luxon';
import { MdPrint } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Button, Heading, Text } from 'scouting-ui/lib';
import { IconButton } from 'scouting-ui/lib/Button';

import type { YouthSubmitResult } from '@modules/meritBadgeCounselors/mbcBulkEntry/duck/types';
import { intl } from '@shared/localization';

import type {
  AppliedClassEntry,
  ClassEntryFormValues,
  ClassRequirement,
  MBClass,
} from '../types';
import { requirementKey, updateableRequirements } from '../utils';
import styles from './ClassEntry.module.scss';
import ClassEntryActionBar from './ClassEntryActionBar';
import ClassFilters from './ClassFilters';
import MBClassLabel from './MBClassLabel';
import RequirementRow, { RequirementLabel } from './RequirementRow';
import SaveErrorsAlert from './SaveErrorsAlert';

function Message(props: { titleId: string; bodyId: string }) {
  return (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <Flex vertical>
          <Text font="body-body-2" strong>
            <FormattedMessage id={props.titleId} />
          </Text>
          <Text font="body-body-3">
            <FormattedMessage id={props.bodyId} />
          </Text>
        </Flex>
      }
    />
  );
}

export interface ClassEntryPageProps {
  classes: MBClass[];
  applied?: AppliedClassEntry;
  requirements: ClassRequirement[];
  disabledReasons: Map<number, string>;
  saveResults: YouthSubmitResult[];
  loadError: boolean;
  loadingClasses: boolean;
  loadingRequirements: boolean;
  isSaving: boolean;
  isPrinting: boolean;
  canPrint: boolean;
  onApply: (applied: AppliedClassEntry) => void;
  onSave: (values: ClassEntryFormValues) => Promise<boolean>;
  onPrint: () => void;
  onRetryLoad: () => void;
}

export default function ClassEntryPage(props: ClassEntryPageProps) {
  const {
    classes,
    applied,
    requirements,
    disabledReasons,
    saveResults,
    loadError,
    loadingClasses,
    loadingRequirements,
    isSaving,
    isPrinting,
    canPrint,
    onApply,
    onSave,
    onPrint,
    onRetryLoad,
  } = props;

  const [form] = Form.useForm<ClassEntryFormValues>();
  const [initialValues] = useState<ClassEntryFormValues>(() => ({
    completionDate: DateTime.now(),
  }));

  const appliedClass = classes.find(mbClass => mbClass.id === applied?.classId);
  const roster = useMemo(() => appliedClass?.scouts ?? [], [appliedClass]);

  const eligibleIds = useMemo(
    () =>
      roster
        .map(youth => youth.memberId)
        .filter(memberId => !disabledReasons.has(memberId)),
    [roster, disabledReasons],
  );
  const updateable = useMemo(
    () => new Set(updateableRequirements(requirements)),
    [requirements],
  );
  const requirementKeys = useMemo(
    () => [...updateable].map(requirementKey),
    [updateable],
  );
  const firstKey = requirementKeys[0];

  // Rows keyed by requirement id survive a version switch when both versions share
  // ids, so the open row is tracked here and reset on every apply
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  useEffect(
    () => setExpandedKeys(firstKey ? [firstKey] : []),
    [firstKey, requirementKeys, applied],
  );

  const toggleExpanded = useCallback(
    (key: string) =>
      setExpandedKeys(prev =>
        prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key],
      ),
    [],
  );

  const handleApply = (next: AppliedClassEntry) => {
    form.resetFields();
    onApply(next);
  };

  const handleSave = async () => {
    if (await onSave(form.getFieldsValue(true))) form.resetFields();
  };

  const renderContent = () => {
    // A failed classes/profile fetch would otherwise read as "no classes" or as
    // every youth being ineligible, both of which look like real answers
    if (loadError) {
      return (
        <Alert
          type="error"
          showIcon
          message={<FormattedMessage id="shared.errors.generic" />}
          action={
            <Button size="small" type="default" onClick={onRetryLoad}>
              <FormattedMessage id="page.mbcClassEntry.retry" />
            </Button>
          }
        />
      );
    }

    if (!loadingClasses && classes.length === 0) {
      return (
        <Message
          titleId="page.mbcClassEntry.noClasses.title"
          bodyId="page.mbcClassEntry.noClasses.body"
        />
      );
    }

    if (!applied || !appliedClass) {
      return (
        <Message
          titleId="page.mbcClassEntry.emptySelection.title"
          bodyId="page.mbcClassEntry.emptySelection.body"
        />
      );
    }

    return (
      <Form
        form={form}
        key={`${applied.classId}-${applied.versionId}`}
        component={false}
        initialValues={initialValues}
      >
        <Flex vertical gap={8}>
          <Flex align="center" justify="space-between" gap={12}>
            <MBClassLabel mbClass={appliedClass} size="lg">
              <Heading font="display-h5" level={2}>
                {appliedClass.label}
              </Heading>
            </MBClassLabel>
            {canPrint && (
              <Tooltip
                title={intl.formatMessage({
                  id: 'page.mbcClassEntry.printClassSheet',
                })}
              >
                <IconButton
                  type="text"
                  size="large"
                  aria-label={intl.formatMessage({
                    id: 'page.mbcClassEntry.printClassSheet',
                  })}
                  loading={isPrinting}
                  icon={<MdPrint className={styles.printIcon} />}
                  onClick={onPrint}
                />
              </Tooltip>
            )}
          </Flex>
          <Text font="body-body-2">
            <FormattedMessage id="page.mbcClassEntry.instructions" />
          </Text>
        </Flex>
        <Alert
          type="info"
          showIcon
          description={
            <FormattedMessage
              id="page.mbcClassEntry.progressNote"
              values={{ br: <br /> }}
            />
          }
        />
        {roster.length > 0 && eligibleIds.length === 0 && (
          <Alert
            type="warning"
            showIcon
            message={
              <FormattedMessage id="page.mbcClassEntry.noEligibleYouth" />
            }
          />
        )}
        {saveResults.length > 0 && (
          <SaveErrorsAlert
            results={saveResults}
            requirements={requirements}
            roster={roster}
          />
        )}

        {loadingRequirements ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : (
          <>
            <ClassEntryActionBar
              requirementKeys={requirementKeys}
              eligibleIds={eligibleIds}
              isSaving={isSaving}
              onReset={() => form.resetFields()}
              onSave={handleSave}
            />
            <Flex vertical gap={16}>
              {requirements.map(req =>
                updateable.has(req) ? (
                  <RequirementRow
                    key={requirementKey(req)}
                    requirement={req}
                    roster={roster}
                    disabledReasons={disabledReasons}
                    eligibleCount={eligibleIds.length}
                    disabled={isSaving}
                    expanded={expandedKeys.includes(requirementKey(req))}
                    onToggleExpanded={toggleExpanded}
                  />
                ) : (
                  <Flex
                    key={requirementKey(req)}
                    gap={12}
                    align="flex-start"
                    className={styles.requirementHeader}
                  >
                    <RequirementLabel requirement={req} />
                  </Flex>
                ),
              )}
            </Flex>
          </>
        )}
      </Form>
    );
  };

  return (
    <Card>
      <Flex vertical gap={20} className={styles.content}>
        <Heading font="display-h4" level={1}>
          <FormattedMessage id="page.mbcClassEntry" />
        </Heading>
        <ClassFilters
          classes={classes}
          loading={loadingClasses}
          applied={applied}
          onApply={handleApply}
        />
        <Divider className={styles.divider} />
        {renderContent()}
      </Flex>
    </Card>
  );
}
